import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react'
import type { AuthError } from '@supabase/supabase-js'
import { SP } from '../../lib/shareTokens'
import { useSession } from '../../lib/session'
import { registerForEventWeb, type RsvpOutcome } from '../../lib/rsvpApi'
import { track } from '../../lib/analytics'
import { setPendingAuth, type PendingAuth } from '../../lib/authReturn'
import {
  PRIVACY_URL,
  TERMS_URL,
  consentMetadata,
  isObfuscatedExistingUser,
  needsTermsAcceptance,
  signupMetadata,
  validateSignupForm,
  type SignupErrors,
} from '../../lib/signupForm'
import type { RsvpStrings } from '../../lib/i18n'
import { buildPendingRsvp } from '../../lib/pendingRsvp'
import StoreBadge from './StoreBadge'
import AppleMark from '../AppleMark'

// ON-407: RSVP from the web event page without the app.
//
//   signup ───────┐                     ┌─ terms (no recorded acceptance, e.g. first Google / Apple sign-in)
//   login ────────┼─ (session appears) ─┤
//   Google/Apple ─┘                     └─ confirm ── register_for_event(p_rsvp_source 'web') ── result
//
// Email signup ends at "check your email": the confirmation link brings the
// runner back to the event page with ?rsvp=confirm, where the page reopens
// this sheet at `confirm`.

export type SheetStep = 'signup' | 'login' | 'forgot' | 'reset_sent' | 'check_email' | 'terms' | 'confirm' | 'result'

export interface RsvpSheetProps {
  s: RsvpStrings
  eventId: string
  eventTitle: string
  /** Absolute event URL with ?rsvp=confirm (+ ref/src) — OAuth and email-link return target. */
  returnUrl: string
  initialStep: 'signup' | 'login' | 'confirm'
  /** Shown above the form, e.g. "Email confirmed — log in to finish your RSVP." */
  initialNotice: string | null
  /** event_id / club_id / ref / src for analytics. */
  analyticsProps: Record<string, unknown>
  /** The share link's ref / src and the page language, carried through signup in pending_rsvp. */
  tracking: { ref?: string; src?: string; lang?: string }
  onClose: () => void
  onOpenApp: () => void
  /** Called with every RSVP outcome; the page drops ?rsvp=confirm on success. */
  onRsvpSettled: (outcome: RsvpOutcome) => void
  /** A password sign-in succeeded (auth_completed, account_match: existing). */
  onPasswordSignIn: () => void
}

const AUTH_STEPS: SheetStep[] = ['signup', 'login', 'forgot', 'reset_sent', 'check_email']

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

type Notice = { tone: 'info' | 'error' | 'success'; text: string } | null

function describeAuthError(s: RsvpStrings, error: AuthError | { message: string }): string {
  const msg = error.message.toLowerCase()
  if (msg.includes('invalid login credentials') || msg.includes('invalid_credentials')) return s.invalidCredentials
  if (msg.includes('email not confirmed')) return s.emailNotConfirmed
  if (msg.includes('already registered') || msg.includes('already been registered')) return s.alreadyRegistered
  if (msg.includes('rate limit') || msg.includes('too many')) return s.rateLimited
  if (msg.includes('network') || msg.includes('failed to fetch')) return s.network
  return s.genericError
}

export default function RsvpSheet(props: RsvpSheetProps) {
  const { s, eventId, eventTitle, returnUrl, analyticsProps, tracking, onClose, onOpenApp, onRsvpSettled, onPasswordSignIn } = props
  const { user, signInWithPassword, signInWithOAuth, signUp, resendSignup, resetPassword, updateMetadata, signOut } =
    useSession()

  const [step, setStep] = useState<SheetStep>(props.initialStep)
  const [notice, setNotice] = useState<Notice>(props.initialNotice ? { tone: 'info', text: props.initialNotice } : null)
  const [busy, setBusy] = useState(false)
  const [canResend, setCanResend] = useState(false)
  const [outcome, setOutcome] = useState<RsvpOutcome | null>(null)

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false)
  const [errors, setErrors] = useState<SignupErrors>({})

  // A session appearing (password sign-in, or a confirmation opened in another
  // tab of this browser) moves an auth step on to the RSVP prompt. Only on the
  // signed-out → signed-in transition, so a deliberate return to `login`
  // (stale session at RSVP time) is not bounced straight back.
  const prevUserId = useRef<string | null>(user?.id ?? null)
  useEffect(() => {
    const id = user?.id ?? null
    if (id && !prevUserId.current && AUTH_STEPS.includes(step)) {
      setNotice(null)
      setStep('confirm')
    }
    prevUserId.current = id
  }, [user, step])

  // The account an in-flight RSVP belongs to may change under it (signed out
  // in another tab): its result is then dropped rather than shown to whoever
  // is signed in now.
  const currentUserId = useRef<string | null>(user?.id ?? null)
  useEffect(() => {
    currentUserId.current = user?.id ?? null
  }, [user])

  // Modal focus: move focus in on open, keep Tab inside, close on Escape, and
  // hand focus back to whatever opened the sheet (the RSVP button) on close.
  const panelRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    panelRef.current?.focus()
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onCloseRef.current()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      const inside = active instanceof Node && panelRef.current.contains(active)
      if (e.shiftKey && (!inside || active === first || active === panelRef.current)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (opener?.isConnected) opener.focus()
    }
  }, [])

  // `confirm` with no recorded terms + privacy acceptance shows the terms step.
  const shown: SheetStep =
    (step === 'confirm' || step === 'terms') && !user
      ? 'login'
      : step === 'confirm' && needsTermsAcceptance(user)
        ? 'terms'
        : step === 'terms' && !needsTermsAcceptance(user)
          ? 'confirm'
          : step

  function go(next: SheetStep, n: Notice = null) {
    setStep(next)
    setNotice(n)
    setErrors({})
    setCanResend(false)
  }

  // Google and Apple share one redirect flow back to `returnUrl`; only Google
  // gets the account-chooser prompt (Apple has no equivalent).
  async function handleOAuth(provider: PendingAuth) {
    if (busy) return
    setBusy(true)
    setNotice(null)
    setPendingAuth(provider)
    const error =
      provider === 'google'
        ? await signInWithOAuth('google', returnUrl, { prompt: 'select_account' })
        : await signInWithOAuth(provider, returnUrl)
    // Only reached when the redirect could not start.
    setBusy(false)
    if (error) setNotice({ tone: 'error', text: describeAuthError(s, error) })
  }

  async function handleSignup(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    const errs = validateSignupForm({ fullName, email, password, confirmPassword, acceptedTerms, acceptedPrivacy })
    setErrors(errs)
    if (Object.keys(errs).length > 0) return
    setBusy(true)
    setNotice(null)
    // The live confirmation template carries no redirect_to, so the event this
    // signup started on rides in user_metadata (pending_rsvp) and /auth/confirm
    // sends the runner back to it. emailRedirectTo is kept for the default
    // template / GoTrue redirect path.
    const metadata = { ...signupMetadata(fullName), pending_rsvp: buildPendingRsvp(eventId, tracking) }
    const { user: created, error } = await signUp(email.trim(), password, metadata, returnUrl)
    setBusy(false)
    if (error) {
      const text = describeAuthError(s, error)
      if (text === s.alreadyRegistered) go('login', { tone: 'info', text })
      else setNotice({ tone: 'error', text })
      return
    }
    // Supabase answers an existing email with a fake, identity-less user
    // (anti-enumeration) — the app detects it the same way.
    if (isObfuscatedExistingUser(created)) {
      setPassword('')
      go('login', { tone: 'info', text: s.alreadyRegistered })
      return
    }
    track('auth_link_sent', { ...analyticsProps, reason: 'signup' })
    go('check_email')
  }

  async function handleResend() {
    if (busy || !email.trim()) return
    setBusy(true)
    const error = await resendSignup(email.trim(), returnUrl)
    setBusy(false)
    if (error) {
      setNotice({ tone: 'error', text: describeAuthError(s, error) })
      return
    }
    track('auth_link_sent', { ...analyticsProps, reason: 'resend' })
    setCanResend(false)
    setNotice({ tone: 'success', text: s.resent })
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    if (!email.trim() || !password) {
      setNotice({ tone: 'error', text: s.invalidCredentials })
      return
    }
    setBusy(true)
    setNotice(null)
    const error = await signInWithPassword(email.trim(), password)
    setBusy(false)
    if (error) {
      const text = describeAuthError(s, error)
      setNotice({ tone: 'error', text })
      setCanResend(text === s.emailNotConfirmed)
      return
    }
    onPasswordSignIn()
    go('confirm')
  }

  async function handleForgot(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    if (!email.trim()) {
      setErrors({ email: 'emailRequired' })
      return
    }
    setBusy(true)
    const error = await resetPassword(email.trim(), `${window.location.origin}/reset-password`)
    setBusy(false)
    if (error) {
      setNotice({ tone: 'error', text: describeAuthError(s, error) })
      return
    }
    go('reset_sent')
  }

  async function handleTerms() {
    if (busy || !acceptedTerms || !acceptedPrivacy) return
    setBusy(true)
    const error = await updateMetadata(consentMetadata())
    setBusy(false)
    if (error) {
      setNotice({ tone: 'error', text: describeAuthError(s, error) })
      return
    }
    go('confirm')
  }

  async function handleConfirm() {
    if (busy) return
    const forUserId = currentUserId.current
    setBusy(true)
    setNotice(null)
    const result = await registerForEventWeb(eventId)
    setBusy(false)
    if (currentUserId.current !== forUserId) return
    onRsvpSettled(result)
    if (result.kind === 'signed_out') {
      // The stored session is stale: clear it here and sign in again.
      await signOut()
      go('login')
      return
    }
    setOutcome(result)
    go('result')
  }

  async function handleSignOut() {
    if (busy) return
    await signOut()
    go('login')
  }

  return (
    <div role="dialog" aria-modal="true" aria-label={eventTitle} style={styles.overlay} onClick={onClose}>
      <div
        ref={panelRef}
        tabIndex={-1}
        style={styles.panel}
        onClick={(e) => e.stopPropagation()}
        data-testid="rsvp-sheet"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={styles.eyebrow}>{eventTitle}</div>
          <button type="button" onClick={onClose} aria-label={s.close} style={styles.close}>
            ×
          </button>
        </div>

        {(shown === 'signup' || shown === 'login') && (
          <div role="tablist" style={styles.tabs}>
            {(['signup', 'login'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={shown === tab}
                onClick={() => go(tab)}
                style={{ ...styles.tab, ...(shown === tab ? styles.tabActive : null) }}
              >
                {tab === 'signup' ? s.createTab : s.loginTab}
              </button>
            ))}
          </div>
        )}

        {notice && (
          <div
            role={notice.tone === 'error' ? 'alert' : 'status'}
            style={{ ...styles.notice, ...(notice.tone === 'error' ? styles.noticeError : notice.tone === 'success' ? styles.noticeSuccess : null) }}
          >
            {notice.text}
          </div>
        )}

        {shown === 'signup' && (
          <>
            <h2 style={styles.title}>{s.createTitle}</h2>
            <p style={styles.sub}>{s.createSubtitle}</p>
            <OAuthButtons s={s} onOAuth={handleOAuth} disabled={busy} />
            <Divider label={s.or} />
            <form onSubmit={handleSignup} noValidate>
              <Field id="rsvp-name" label={s.fullName} value={fullName} onChange={setFullName} autoComplete="name" error={errors.fullName && s.validation[errors.fullName]} />
              <Field id="rsvp-email" label={s.email} type="email" value={email} onChange={setEmail} autoComplete="email" error={errors.email && s.validation[errors.email]} />
              <Field id="rsvp-password" label={s.password} placeholder={s.passwordPlaceholder} type="password" value={password} onChange={setPassword} autoComplete="new-password" error={errors.password && s.validation[errors.password]} />
              <Field id="rsvp-confirm-password" label={s.confirmPassword} type="password" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" error={errors.confirmPassword && s.validation[errors.confirmPassword]} />
              <Consent s={s} terms={acceptedTerms} privacy={acceptedPrivacy} onTerms={setAcceptedTerms} onPrivacy={setAcceptedPrivacy} errors={errors} />
              <PrimaryButton type="submit" disabled={busy}>{busy ? s.creatingAccount : s.createAccount}</PrimaryButton>
            </form>
          </>
        )}

        {shown === 'login' && (
          <>
            <h2 style={styles.title}>{s.loginTitle}</h2>
            <OAuthButtons s={s} onOAuth={handleOAuth} disabled={busy} />
            <Divider label={s.or} />
            <form onSubmit={handleLogin} noValidate>
              <Field id="rsvp-email" label={s.email} type="email" value={email} onChange={setEmail} autoComplete="email" />
              <Field id="rsvp-password" label={s.password} type="password" value={password} onChange={setPassword} autoComplete="current-password" />
              <PrimaryButton type="submit" disabled={busy}>{busy ? s.signingIn : s.signIn}</PrimaryButton>
            </form>
            {canResend && (
              <SecondaryButton onClick={handleResend} disabled={busy}>{s.resendConfirmation}</SecondaryButton>
            )}
            <button type="button" onClick={() => go('forgot')} style={styles.link}>
              {s.forgotPassword}
            </button>
          </>
        )}

        {shown === 'forgot' && (
          <form onSubmit={handleForgot} noValidate>
            <h2 style={styles.title}>{s.forgotPassword}</h2>
            <Field id="rsvp-email" label={s.email} type="email" value={email} onChange={setEmail} autoComplete="email" error={errors.email && s.validation[errors.email]} />
            <PrimaryButton type="submit" disabled={busy}>{s.sendResetLink}</PrimaryButton>
            <button type="button" onClick={() => go('login')} style={styles.link}>
              {s.backToLogin}
            </button>
          </form>
        )}

        {shown === 'reset_sent' && (
          <>
            <p style={styles.body}>{s.resetSent(email.trim())}</p>
            <SecondaryButton onClick={() => go('login')}>{s.backToLogin}</SecondaryButton>
          </>
        )}

        {shown === 'check_email' && (
          <>
            <h2 style={styles.title}>{s.checkEmailTitle}</h2>
            <p style={styles.body}>{s.checkEmailBody(email.trim())}</p>
            <p style={styles.sub}>{s.checkEmailHint}</p>
            <SecondaryButton onClick={handleResend} disabled={busy}>{s.resendConfirmation}</SecondaryButton>
          </>
        )}

        {shown === 'terms' && (
          <>
            <h2 style={styles.title}>{s.termsTitle}</h2>
            <p style={styles.sub}>{s.termsBody}</p>
            <Consent s={s} terms={acceptedTerms} privacy={acceptedPrivacy} onTerms={setAcceptedTerms} onPrivacy={setAcceptedPrivacy} errors={{}} />
            <PrimaryButton onClick={handleTerms} disabled={busy || !acceptedTerms || !acceptedPrivacy}>{s.continue}</PrimaryButton>
          </>
        )}

        {shown === 'confirm' && (
          <>
            <h2 style={styles.title}>{s.confirmTitle}</h2>
            <p style={styles.body}>{eventTitle}</p>
            {user?.email && <p style={styles.sub}>{s.signedInAs(user.email)}</p>}
            <PrimaryButton onClick={handleConfirm} disabled={busy}>{busy ? s.confirming : s.confirmButton}</PrimaryButton>
            <button type="button" onClick={handleSignOut} disabled={busy} style={styles.link}>
              {s.signOut}
            </button>
          </>
        )}

        {shown === 'result' && outcome && (
          <Result s={s} outcome={outcome} onOpenApp={onOpenApp} onDone={onClose} onRetry={() => go('confirm')} />
        )}
      </div>
    </div>
  )
}

function Result({
  s,
  outcome,
  onOpenApp,
  onDone,
  onRetry,
}: {
  s: RsvpStrings
  outcome: RsvpOutcome
  onOpenApp: () => void
  onDone: () => void
  onRetry: () => void
}) {
  const appCta = (
    <>
      <PrimaryButton onClick={onOpenApp}>{s.openInApp}</PrimaryButton>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 4 }}>
        <StoreBadge platform="ios" height={40} />
        <StoreBadge platform="android" height={40} />
      </div>
    </>
  )
  switch (outcome.kind) {
    case 'registered':
      return (
        <div data-testid="rsvp-result" data-outcome={outcome.kind}>
          <h2 style={styles.title}>{s.registeredTitle}</h2>
          <p style={styles.body}>{s.registeredBody}</p>
          {outcome.pickupCode && <p style={styles.code}>{s.pickupCode(outcome.pickupCode)}</p>}
          {outcome.hasClaim && <p style={styles.sub}>{s.claimNote}</p>}
          {appCta}
        </div>
      )
    case 'waitlisted':
    case 'already_registered':
    case 'already_waitlisted':
      return (
        <div data-testid="rsvp-result" data-outcome={outcome.kind}>
          <h2 style={styles.title}>
            {outcome.kind === 'waitlisted'
              ? s.waitlistedTitle
              : outcome.kind === 'already_registered'
                ? s.alreadyRegisteredTitle
                : s.alreadyWaitlistedTitle}
          </h2>
          {outcome.kind === 'waitlisted' && <p style={styles.body}>{s.waitlistedBody}</p>}
          {appCta}
        </div>
      )
    case 'needs_app':
      return (
        <div data-testid="rsvp-result" data-outcome={outcome.kind}>
          <h2 style={styles.title}>{s.needsAppTitle}</h2>
          <p style={styles.body}>{s.needsAppBody}</p>
          <PrimaryButton onClick={onOpenApp}>{s.rsvpInApp}</PrimaryButton>
        </div>
      )
    case 'closed':
    case 'not_found':
      return (
        <div data-testid="rsvp-result" data-outcome={outcome.kind}>
          <h2 style={styles.title}>{s.closedTitle}</h2>
          <p style={styles.body}>{outcome.kind === 'closed' ? s.closedBody : s.notFoundBody}</p>
          <SecondaryButton onClick={onDone}>{s.done}</SecondaryButton>
        </div>
      )
    default:
      return (
        <div data-testid="rsvp-result" data-outcome={outcome.kind}>
          <h2 style={styles.title}>{s.errorTitle}</h2>
          <p style={styles.body}>{s.genericError}</p>
          <PrimaryButton onClick={onRetry}>{s.tryAgain}</PrimaryButton>
        </div>
      )
  }
}

function Field(props: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  autoComplete?: string
  error?: string | false
}) {
  const errId = `${props.id}-error`
  return (
    <div style={{ marginBottom: 12 }}>
      <label htmlFor={props.id} style={styles.label}>
        {props.label}
      </label>
      <input
        id={props.id}
        type={props.type ?? 'text'}
        value={props.value}
        placeholder={props.placeholder}
        autoComplete={props.autoComplete}
        onChange={(e) => props.onChange(e.target.value)}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={props.error ? errId : undefined}
        style={{ ...styles.input, ...(props.error ? { borderColor: '#FF6B6B' } : null) }}
      />
      {props.error && (
        <div id={errId} style={styles.fieldError}>
          {props.error}
        </div>
      )}
    </div>
  )
}

function Consent({
  s,
  terms,
  privacy,
  onTerms,
  onPrivacy,
  errors,
}: {
  s: RsvpStrings
  terms: boolean
  privacy: boolean
  onTerms: (v: boolean) => void
  onPrivacy: (v: boolean) => void
  errors: SignupErrors
}) {
  const row = (checked: boolean, set: (v: boolean) => void, linkLabel: string, href: string, error?: string) => (
    <div style={{ marginBottom: 10 }}>
      <label style={styles.check}>
        <input type="checkbox" checked={checked} onChange={(e) => set(e.target.checked)} style={{ accentColor: SP.lime, width: 18, height: 18, flexShrink: 0 }} />
        <span>
          {s.iAccept}{' '}
          <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: SP.lime }}>
            {linkLabel}
          </a>
        </span>
      </label>
      {error && <div style={styles.fieldError}>{error}</div>}
    </div>
  )
  return (
    <div style={{ margin: '4px 0 14px' }}>
      {row(terms, onTerms, s.termsAndConditions, TERMS_URL, errors.terms && s.validation[errors.terms])}
      {row(privacy, onPrivacy, s.privacyPolicy, PRIVACY_URL, errors.privacy && s.validation[errors.privacy])}
    </div>
  )
}

function PrimaryButton(props: { children: ReactNode; onClick?: () => void; disabled?: boolean; type?: 'button' | 'submit' }) {
  return (
    <button type={props.type ?? 'button'} onClick={props.onClick} disabled={props.disabled} style={{ ...styles.primary, opacity: props.disabled ? 0.6 : 1 }}>
      {props.children}
    </button>
  )
}

function SecondaryButton(props: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={props.onClick} disabled={props.disabled} style={{ ...styles.secondary, opacity: props.disabled ? 0.6 : 1 }}>
      {props.children}
    </button>
  )
}

function OAuthButtons({ s, onOAuth, disabled }: { s: RsvpStrings; onOAuth: (p: PendingAuth) => void; disabled?: boolean }) {
  return (
    <>
      <GoogleButton label={s.continueWithGoogle} onClick={() => onOAuth('google')} disabled={disabled} />
      <AppleButton label={s.continueWithApple} onClick={() => onOAuth('apple')} disabled={disabled} />
    </>
  )
}

// Apple HIG: black button, white Apple logo, "Continue with Apple" — same look
// as the captain portal's .cp-btn-apple.
function AppleButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{ ...styles.secondary, ...styles.apple, opacity: disabled ? 0.6 : 1 }}
    >
      <AppleMark size={17} />
      {label}
    </button>
  )
}

function GoogleButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} style={{ ...styles.secondary, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
      </svg>
      {label}
    </button>
  )
}

function Divider({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', color: SP.dim, fontSize: 11, fontFamily: SP.mono, letterSpacing: 1.5, textTransform: 'uppercase' }}>
      <span style={{ flex: 1, height: 1, background: SP.border }} />
      {label}
      <span style={{ flex: 1, height: 1, background: SP.border }} />
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  panel: {
    outline: 'none',
    width: '100%',
    maxWidth: 480,
    maxHeight: '92vh',
    overflowY: 'auto',
    background: SP.bgRaised,
    color: SP.text,
    fontFamily: SP.ui,
    WebkitFontSmoothing: 'antialiased',
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    border: `1px solid ${SP.borderBright}`,
    borderBottom: 'none',
    padding: '18px 20px calc(24px + env(safe-area-inset-bottom))',
    boxSizing: 'border-box',
  },
  eyebrow: {
    fontSize: 11,
    fontFamily: SP.mono,
    color: SP.lime,
    letterSpacing: 2,
    textTransform: 'uppercase',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  close: {
    background: 'none',
    border: 'none',
    color: SP.muted,
    fontSize: 26,
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 4px',
    flexShrink: 0,
  },
  tabs: {
    display: 'flex',
    gap: 4,
    padding: 4,
    borderRadius: 999,
    background: SP.card,
    border: `1px solid ${SP.border}`,
    marginBottom: 16,
  },
  tab: {
    flex: 1,
    padding: '9px 10px',
    borderRadius: 999,
    border: 'none',
    background: 'transparent',
    color: SP.muted,
    fontFamily: SP.ui,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  tabActive: { background: SP.cardRaised, color: SP.text },
  title: { fontSize: 22, fontWeight: 800, letterSpacing: -0.6, margin: '0 0 6px' },
  sub: { fontSize: 13, color: SP.muted, lineHeight: 1.5, margin: '0 0 14px' },
  body: { fontSize: 15, color: SP.text, lineHeight: 1.5, margin: '0 0 12px' },
  code: {
    fontFamily: SP.mono,
    fontSize: 16,
    color: SP.lime,
    letterSpacing: 1,
    margin: '0 0 12px',
  },
  notice: {
    fontSize: 13,
    lineHeight: 1.5,
    padding: '10px 12px',
    borderRadius: 12,
    background: SP.card,
    border: `1px solid ${SP.borderBright}`,
    marginBottom: 14,
  },
  noticeError: { borderColor: 'rgba(255,107,107,0.5)', color: '#FFB3B3' },
  noticeSuccess: { borderColor: `${SP.lime}66`, color: SP.lime },
  label: {
    display: 'block',
    fontSize: 11,
    fontFamily: SP.mono,
    color: SP.muted,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px 14px',
    borderRadius: 12,
    border: `1px solid ${SP.borderBright}`,
    background: SP.card,
    color: SP.text,
    fontFamily: SP.ui,
    // 16px keeps iOS Safari from zooming on focus.
    fontSize: 16,
    outline: 'none',
  },
  fieldError: { color: '#FF8A8A', fontSize: 12, marginTop: 5 },
  check: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: SP.text, cursor: 'pointer' },
  primary: {
    width: '100%',
    padding: '14px 18px',
    borderRadius: 999,
    border: 'none',
    background: SP.lime,
    color: SP.charcoal,
    fontFamily: SP.ui,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 4,
    marginBottom: 10,
  },
  secondary: {
    width: '100%',
    padding: '13px 18px',
    borderRadius: 999,
    border: `1px solid ${SP.borderBright}`,
    background: 'transparent',
    color: SP.text,
    fontFamily: SP.ui,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 10,
  },
  apple: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    background: '#000000',
    color: '#ffffff',
    border: '1px solid rgba(244,242,242,0.25)',
  },
  link: {
    display: 'block',
    margin: '4px auto 0',
    background: 'none',
    border: 'none',
    color: SP.muted,
    fontFamily: SP.ui,
    fontSize: 13,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}
